import React, { useState } from "react";
import { delegationsAPI } from "../api/client.js";
import { toast } from "react-toastify";
import "./DelegateModal.css";

// Maps the numeric workflow stage (0/1/2/3) stored in the DB
// to a human-readable label for the UI. Purely cosmetic —
// if removed, the `|| `Stage ${stage}`` fallbacks still work
// and just show "Stage 1/2/3".
const STAGE_LABELS = {
  0: "All Stages",
  1: "Stage 1 - Hiring Manager",
  2: "Stage 2 - BU Head",
  3: "Stage 3 - HR Review",
};

export default function DelegateModal({ stage, offer, onClose, onDelegated }) {
  const [delegateSearch, setDelegateSearch] = useState("");
  const [delegateResults, setDelegateResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedDelegate, setSelectedDelegate] = useState(null);

  const [delegationType, setDelegationType] = useState("general");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSearch = async (value) => {
    setDelegateSearch(value);
    if (value.length < 2) {
      setDelegateResults([]);
      setShowDropdown(false);
      return;
    }
    try {
      const res = await delegationsAPI.searchUsers(value);
      setDelegateResults(res.data?.users || []);
      setShowDropdown(true);
    } catch {
      setDelegateResults([]);
    }
  };

  const selectDelegate = (user) => {
    setSelectedDelegate(user);
    setDelegateSearch(user.name ? `${user.name} (${user.email})` : user.email);
    setShowDropdown(false);
  };

  const handleSubmit = async () => {
    if (!selectedDelegate) {
      toast.error("Please select a delegate");
      return;
    }
    if (delegationType === "conditional" && (!startDate || !endDate)) {
      toast.error("Please select start and end dates");
      return;
    }

    setSubmitting(true);
    try {
      await delegationsAPI.create({
        delegate_email: selectedDelegate.email,
        delegation_type: delegationType,
        stage: stage,
        start_date: delegationType === "conditional" ? startDate : undefined,
        end_date: delegationType === "conditional" ? endDate : undefined,
        reason: reason,
      });
      toast.success(`Delegation created for ${STAGE_LABELS[stage]}`);
      onDelegated?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to create delegation");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dm-overlay" onClick={onClose}>
      <div className="dm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dm-header">
          <h3>Delegate {STAGE_LABELS[stage] || `Stage ${stage}`}</h3>
          <button className="dm-close" onClick={onClose}>×</button>
        </div>

        <div className="dm-body">
          {/* Current approver info */}
          <div className="dm-info-bar">
            <span className="dm-info-label">Current Approver:</span>
            <span className="dm-info-value">
              {offer?.[`stage${stage}_email`] || offer?.hiring_manager_email || "—"}
            </span>
          </div>

          {/* Offer info */}
          <div className="dm-info-bar">
            <span className="dm-info-label">Candidate:</span>
            <span className="dm-info-value">
              {offer?.candidate_name || "—"}
            </span>
          </div>

          {/* Delegate search */}
          <div className="dm-field">
            <label>Delegate To *</label>
            <div className="dm-search-wrap">
              <input
                type="text"
                placeholder="Search by name or email..."
                value={delegateSearch}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => delegateResults.length > 0 && setShowDropdown(true)}
              />
              {showDropdown && delegateResults.length > 0 && (
                <div className="dm-dropdown">
                  {delegateResults.map((u) => (
                    <div
                      key={u.id}
                      className="dm-option"
                      onClick={() => selectDelegate(u)}
                    >
                      <span className="dm-opt-name">{u.name}</span>
                      <span className="dm-opt-email">{u.email}</span>
                      <span className="dm-opt-role">{u.role}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Delegation type */}
          <div className="dm-field">
            <label>Type *</label>
            <select
              value={delegationType}
              onChange={(e) => setDelegationType(e.target.value)}
            >
              <option value="general">General (No Expiry)</option>
              <option value="conditional">Conditional (Date Range)</option>
            </select>
          </div>

          {/* Conditional dates */}
          {delegationType === "conditional" && (
            <div className="dm-date-row">
              <div className="dm-field">
                <label>Start Date *</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="dm-field">
                <label>End Date *</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Reason */}
          <div className="dm-field">
            <label>Reason</label>
            <textarea
              rows={3}
              placeholder="Optional reason..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
            />
            <div className="dm-counter">{reason.length}/500</div>
          </div>
        </div>

        <div className="dm-footer">
          <button className="dm-btn cancel" onClick={onClose}>Cancel</button>
          <button
            className="dm-btn primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Creating..." : "Delegate"}
          </button>
        </div>
      </div>
    </div>
  );
}
